<p-dialog
  [visible]="visible"
  (visibleChange)="visibleChange.emit($event)"
  [modal]="true"
  header="Execute Field Work"
  [draggable]="false"
  [resizable]="false"
  [closable]="!busy()"
  [closeOnEscape]="!busy()"
  appendTo="body"
  [style]="{ width: 'min(1200px, 96vw)' }"
  [breakpoints]="{ '900px': '97vw' }"
  styleClass="master-create-dialog field-execution-dialog">
  <section class="master-dialog-body">
    <aside class="master-dialog-summary">
      <div class="summary-icon"><i class="pi pi-play-circle"></i></div>
      <div>
        <small>Field execution</small>
        <h3>{{ currentWork()?.workPackageNumber || 'Work package' }}</h3>
        <p>{{ currentWork()?.title }}</p>
      </div>
      <div class="master-dialog-facts">
        <div><span>Status</span><p-tag [value]="status" [severity]="severity(status)"></p-tag></div>
        <div><span>Labor actuals</span><strong>{{ laborProgress().recorded }}/{{ laborProgress().total }} recorded</strong></div>
        <div><span>Equipment usage</span><strong>{{ equipmentProgress().usageRecorded }}/{{ equipmentProgress().total }} recorded</strong></div>
        <div><span>Equipment returned</span><strong>{{ equipmentProgress().returned }}/{{ equipmentProgress().total }}</strong></div>
      </div>
      <div class="next-action">
        <span>What to do next</span>
        <strong>{{ nextActionTitle }}</strong>
        <p>{{ nextActionText }}</p>
      </div>
    </aside>

    <div class="execution-body">
      <div class="workflow-steps">
        <div [class.done]="status !== 'RELEASED'" [class.active]="status === 'RELEASED'">
          <span>1</span><strong>Start</strong><small>Begin field execution</small>
        </div>
        <div [class.done]="status === 'COMPLETED'" [class.active]="status === 'IN_PROGRESS'">
          <span>2</span><strong>Execute</strong><small>People, custody and usage</small>
        </div>
        <div [class.active]="status === 'COMPLETED'">
          <span>3</span><strong>Approve</strong><small>Actuals and finance handoff</small>
        </div>
      </div>

      @if (error()) {
        <div class="dialog-message error"><i class="pi pi-exclamation-circle"></i><span>{{ error() }}</span></div>
      }
      @if (message()) {
        <div class="dialog-message success"><i class="pi pi-check-circle"></i><span>{{ message() }}</span></div>
      }
      @if (loading()) {
        <div class="loading-state"><i class="pi pi-spin pi-spinner"></i> Loading execution records...</div>
      } @else {
        <section class="execution-section">
          <div class="section-heading">
            <div><span>People</span><h4>Labor actuals</h4></div>
            <small>One traceable actual record is maintained per field assignment.</small>
          </div>

          <div class="execution-list">
            @for (assignment of currentWork()?.laborAssignments || []; track assignment.fieldLaborAssignmentId) {
              @let entry = entryFor(assignment.fieldLaborAssignmentId);
              <article class="execution-card">
                <div class="resource-heading">
                  <div class="resource-icon"><i class="pi pi-user"></i></div>
                  <div>
                    <strong>{{ assignment.workerName }}</strong>
                    <small>{{ assignment.sourceType }} · {{ assignment.billingUnit || 'TASK' }}</small>
                  </div>
                  <p-tag [value]="entry?.entryStatus || 'NOT RECORDED'"
                    [severity]="entry ? severity(entry.entryStatus) : 'secondary'"></p-tag>
                </div>

                @if (canExecute && status !== 'RELEASED' && (!entry || entry.entryStatus === 'DRAFT' || entry.entryStatus === 'REJECTED')) {
                  @let laborEvidenceKey = evidenceKey('labor', assignment.fieldLaborAssignmentId);
                  <div class="actual-form">
                    <p-inputgroup>
                      <p-inputgroup-addon><i class="pi pi-calendar"></i></p-inputgroup-addon>
                      <p-iftalabel>
                        <p-datepicker [inputId]="'labor-work-date-' + assignment.fieldLaborAssignmentId"
                          [(ngModel)]="laborDates[assignment.fieldLaborAssignmentId!]" dateFormat="dd M yy"
                          appendTo="body" [showIcon]="true"></p-datepicker>
                        <label [for]="'labor-work-date-' + assignment.fieldLaborAssignmentId">Work date</label>
                      </p-iftalabel>
                    </p-inputgroup>
                    <p-inputgroup>
                      <p-inputgroup-addon><i class="pi pi-calculator"></i></p-inputgroup-addon>
                      <p-iftalabel>
                        <p-inputNumber [inputId]="'labor-quantity-' + assignment.fieldLaborAssignmentId"
                          [(ngModel)]="laborForm(assignment).actualQuantity" [min]="0.01"
                          [minFractionDigits]="0" [maxFractionDigits]="2"></p-inputNumber>
                        <label [for]="'labor-quantity-' + assignment.fieldLaborAssignmentId">Actual quantity</label>
                      </p-iftalabel>
                    </p-inputgroup>
                    <p-inputgroup class="evidence-input-group">
                      <p-inputgroup-addon><i class="pi pi-paperclip"></i></p-inputgroup-addon>
                      <p-iftalabel>
                        <input pInputText [id]="'labor-evidence-' + assignment.fieldLaborAssignmentId" readonly
                          [value]="evidenceFileName(laborEvidenceKey, laborForm(assignment).evidenceUrl)">
                        <label [for]="'labor-evidence-' + assignment.fieldLaborAssignmentId">Evidence file</label>
                      </p-iftalabel>
                      <input #laborEvidence type="file" class="evidence-file-input"
                        accept="image/*,application/pdf,.doc,.docx,.xls,.xlsx,.txt"
                        (change)="selectEvidence($event, laborForm(assignment), laborEvidenceKey)">
                      <button pButton type="button" icon="pi pi-upload" label="Upload" size="small"
                        [loading]="isEvidenceUploading(laborEvidenceKey)" (click)="laborEvidence.click()"></button>
                      @if (laborForm(assignment).evidenceUrl) {
                        <button pButton type="button" icon="pi pi-times" severity="secondary" text
                          aria-label="Remove labor evidence" (click)="clearEvidence(laborForm(assignment), laborEvidenceKey)"></button>
                      }
                    </p-inputgroup>
                    <p-inputgroup>
                      <p-inputgroup-addon><i class="pi pi-file-edit"></i></p-inputgroup-addon>
                      <p-iftalabel>
                        <textarea pTextarea [id]="'labor-notes-' + assignment.fieldLaborAssignmentId" rows="1"
                          [(ngModel)]="laborForm(assignment).notes"></textarea>
                        <label [for]="'labor-notes-' + assignment.fieldLaborAssignmentId">Execution notes</label>
                      </p-iftalabel>
                    </p-inputgroup>
                  </div>
                  <div class="card-actions">
                    <button pButton type="button" label="Save actual" icon="pi pi-save" size="small"
                      [loading]="isAction('labor-save-' + assignment.fieldLaborAssignmentId)"
                      [disabled]="isEvidenceUploading(laborEvidenceKey)"
                      (click)="saveLaborActual(assignment)"></button>
                    @if (entry; as savedEntry) {
                      <button pButton type="button" label="Submit for approval" icon="pi pi-send" size="small" outlined
                        [loading]="isAction('labor-submit-' + savedEntry.fieldLaborWorkEntryId)"
                        (click)="laborAction(savedEntry, 'submit')"></button>
                    }
                  </div>
                  @if (entry?.entryStatus === 'REJECTED') {
                    <small class="rejection-note">Rejected: {{ entry?.rejectionReason || 'No reason provided' }}</small>
                  }
                }

                @if (entry?.entryStatus === 'SUBMITTED' && canApprove) {
                  <div class="decision-row">
                    <p-inputgroup>
                      <p-inputgroup-addon><i class="pi pi-comment"></i></p-inputgroup-addon>
                      <p-iftalabel>
                        <input pInputText [id]="'labor-decision-' + entry?.fieldLaborWorkEntryId"
                          [(ngModel)]="decisionReasons[decisionKey('labor', entry?.fieldLaborWorkEntryId)]">
                        <label [for]="'labor-decision-' + entry?.fieldLaborWorkEntryId">Reason if rejecting</label>
                      </p-iftalabel>
                    </p-inputgroup>
                    <button pButton type="button" label="Reject" icon="pi pi-times" severity="danger" outlined size="small"
                      [loading]="isAction('labor-reject-' + entry?.fieldLaborWorkEntryId)"
                      (click)="laborAction(entry!, 'reject')"></button>
                    <button pButton type="button" label="Approve" icon="pi pi-check" size="small"
                      [loading]="isAction('labor-approve-' + entry?.fieldLaborWorkEntryId)"
                      (click)="laborAction(entry!, 'approve')"></button>
                  </div>
                }

                @if (entry?.entryStatus === 'APPROVED' && canApprove) {
                  <div class="decision-row reversal-row">
                    <p-inputgroup>
                      <p-inputgroup-addon><i class="pi pi-comment"></i></p-inputgroup-addon>
                      <p-iftalabel>
                        <input pInputText [id]="'labor-reversal-' + entry?.fieldLaborWorkEntryId"
                          [(ngModel)]="decisionReasons[decisionKey('labor', entry?.fieldLaborWorkEntryId)]">
                        <label [for]="'labor-reversal-' + entry?.fieldLaborWorkEntryId">Correction or reversal reason</label>
                      </p-iftalabel>
                    </p-inputgroup>
                    <button pButton type="button" label="Reverse actual" icon="pi pi-undo" severity="danger" outlined size="small"
                      [loading]="isAction('labor-reverse-' + entry?.fieldLaborWorkEntryId)"
                      (click)="laborAction(entry!, 'reverse')"></button>
                  </div>
                }

                @if (canViewRates && entry) {
                  <div class="amounts">
                    <span>Quantity <strong>{{ entry.actualQuantity }} {{ entry.billingUnit }}</strong></span>
                    <span>Cost <strong>{{ entry.buyAmount | number:'1.2-2' }} {{ assignment.currencyCode }}</strong></span>
                    <span>Revenue <strong>{{ entry.sellAmount | number:'1.2-2' }} {{ assignment.currencyCode }}</strong></span>
                  </div>
                }
              </article>
            } @empty {
              <div class="empty-state"><i class="pi pi-users"></i><strong>No assigned people</strong>
                <span>Return to resource planning to assign labor before execution.</span></div>
            }
          </div>
        </section>

        @if (canViewEquipment) {
          <section class="execution-section">
            <div class="section-heading">
              <div><span>Reusable resources</span><h4>Equipment custody and usage</h4></div>
              <small>Checkout and return actions retain actors, timestamps, condition and evidence.</small>
            </div>

            <div class="execution-list">
              @for (allocation of currentWork()?.equipmentAllocations || []; track allocation.fieldEquipmentAllocationId) {
                @let usage = usageFor(allocation.fieldEquipmentAllocationId);
                <article class="execution-card equipment-card">
                  <div class="resource-heading">
                    <div class="resource-icon equipment-icon"><i class="pi pi-wrench"></i></div>
                    <div>
                      <strong>{{ allocation.equipmentResourceName }}</strong>
                      <small>{{ allocation.ownershipType }} · {{ allocation.plannedQuantity }} {{ allocation.billingUnit || 'TASK' }}</small>
                    </div>
                    <p-tag [value]="allocation.allocationStatus || 'RESERVED'"
                      [severity]="severity(allocation.allocationStatus)"></p-tag>
                  </div>

                  @if (canCheckout && (allocation.allocationStatus === 'RESERVED' || allocation.allocationStatus === 'ISSUED')) {
                    <div class="substitution-block">
                      @if (!substitutionCandidates[allocation.fieldEquipmentAllocationId!]) {
                        <button pButton type="button" label="Find replacement" icon="pi pi-sync" size="small" text
                          [loading]="isAction('equipment-substitutes-' + allocation.fieldEquipmentAllocationId)"
                          (click)="loadSubstitutionCandidates(allocation)"></button>
                      } @else {
                        <p-inputgroup>
                          <p-inputgroup-addon><i class="pi pi-wrench"></i></p-inputgroup-addon>
                          <p-iftalabel>
                            <p-select [inputId]="'replacement-' + allocation.fieldEquipmentAllocationId"
                              [options]="substitutionCandidates[allocation.fieldEquipmentAllocationId!]"
                              optionLabel="resourceName" optionValue="equipmentResourceId" [filter]="true"
                              [(ngModel)]="substitutionResourceIds[allocation.fieldEquipmentAllocationId!]" appendTo="body">
                          <ng-template #item let-resource>
                            <div><strong>{{ resource.resourceName }}</strong><small>{{ resource.resourceCode }} · {{ resource.currentLocationName || 'No location' }}</small></div>
                          </ng-template>
                            </p-select>
                            <label [for]="'replacement-' + allocation.fieldEquipmentAllocationId">Available replacement</label>
                          </p-iftalabel>
                        </p-inputgroup>
                        <p-inputgroup>
                          <p-inputgroup-addon><i class="pi pi-comment"></i></p-inputgroup-addon>
                          <p-iftalabel>
                            <input pInputText [id]="'substitution-reason-' + allocation.fieldEquipmentAllocationId"
                              [(ngModel)]="substitutionReasons[allocation.fieldEquipmentAllocationId!]">
                            <label [for]="'substitution-reason-' + allocation.fieldEquipmentAllocationId">Audited substitution reason</label>
                          </p-iftalabel>
                        </p-inputgroup>
                        <button pButton type="button" label="Substitute" icon="pi pi-sync" size="small" outlined
                          [loading]="isAction('equipment-substitute-' + allocation.fieldEquipmentAllocationId)"
                          [disabled]="!substitutionCandidates[allocation.fieldEquipmentAllocationId!].length"
                          (click)="substituteEquipment(allocation)"></button>
                      }
                    </div>
                  }

                  @if (canCheckout && allocation.allocationStatus === 'RESERVED') {
                    <div class="custody-form">
                      <p-inputgroup>
                        <p-inputgroup-addon><i class="pi pi-shield"></i></p-inputgroup-addon>
                        <p-iftalabel>
                          <p-select [inputId]="'issue-condition-' + allocation.fieldEquipmentAllocationId"
                            [options]="conditions" optionLabel="label" optionValue="value"
                            [(ngModel)]="custodyForm(allocation).issueCondition" appendTo="body"></p-select>
                          <label [for]="'issue-condition-' + allocation.fieldEquipmentAllocationId">Outgoing condition</label>
                        </p-iftalabel>
                      </p-inputgroup>
                      <p-inputgroup>
                        <p-inputgroup-addon><i class="pi pi-user"></i></p-inputgroup-addon>
                        <p-iftalabel>
                          <p-select [inputId]="'custodian-' + allocation.fieldEquipmentAllocationId"
                            [options]="assignmentOptions()" optionLabel="label" optionValue="fieldLaborAssignmentId"
                            [(ngModel)]="custodyForm(allocation).custodianLaborAssignmentId" [showClear]="true"
                            appendTo="body"></p-select>
                          <label [for]="'custodian-' + allocation.fieldEquipmentAllocationId">Custodian (optional)</label>
                        </p-iftalabel>
                      </p-inputgroup>
                      <button pButton type="button" label="Issue equipment" icon="pi pi-sign-out" size="small"
                        [loading]="isAction('equipment-issue-' + allocation.fieldEquipmentAllocationId)"
                        (click)="equipmentAction(allocation, 'issue')"></button>
                    </div>
                  }

                  @if (canCheckout && allocation.allocationStatus === 'ISSUED') {
                    <div class="card-actions">
                      <button pButton type="button" label="Start usage" icon="pi pi-play" size="small"
                        [loading]="isAction('equipment-start-' + allocation.fieldEquipmentAllocationId)"
                        (click)="equipmentAction(allocation, 'start')"></button>
                    </div>
                  }

                  @if (canCheckout && (allocation.allocationStatus === 'ISSUED' || allocation.allocationStatus === 'IN_USE')) {
                    @let returnEvidenceKey = evidenceKey('return', allocation.fieldEquipmentAllocationId);
                    <div class="return-form">
                      <p-inputgroup>
                        <p-inputgroup-addon><i class="pi pi-shield"></i></p-inputgroup-addon>
                        <p-iftalabel>
                          <p-select [inputId]="'return-condition-' + allocation.fieldEquipmentAllocationId"
                            [options]="conditions" optionLabel="label" optionValue="value"
                            [(ngModel)]="custodyForm(allocation).returnCondition" appendTo="body"></p-select>
                          <label [for]="'return-condition-' + allocation.fieldEquipmentAllocationId">Incoming condition</label>
                        </p-iftalabel>
                      </p-inputgroup>
                      <p-inputgroup>
                        <p-inputgroup-addon><i class="pi pi-exclamation-triangle"></i></p-inputgroup-addon>
                        <p-iftalabel>
                          <p-inputNumber [inputId]="'damaged-quantity-' + allocation.fieldEquipmentAllocationId"
                            [(ngModel)]="custodyForm(allocation).damagedQuantity" [min]="0"
                            [max]="allocation.plannedQuantity || 1"></p-inputNumber>
                          <label [for]="'damaged-quantity-' + allocation.fieldEquipmentAllocationId">Damaged quantity</label>
                        </p-iftalabel>
                      </p-inputgroup>
                      <p-inputgroup>
                        <p-inputgroup-addon><i class="pi pi-times-circle"></i></p-inputgroup-addon>
                        <p-iftalabel>
                          <p-inputNumber [inputId]="'lost-quantity-' + allocation.fieldEquipmentAllocationId"
                            [(ngModel)]="custodyForm(allocation).lostQuantity" [min]="0"
                            [max]="allocation.plannedQuantity || 1"></p-inputNumber>
                          <label [for]="'lost-quantity-' + allocation.fieldEquipmentAllocationId">Lost quantity</label>
                        </p-iftalabel>
                      </p-inputgroup>
                      <p-inputgroup class="evidence-input-group">
                        <p-inputgroup-addon><i class="pi pi-paperclip"></i></p-inputgroup-addon>
                        <p-iftalabel>
                          <input pInputText [id]="'return-evidence-' + allocation.fieldEquipmentAllocationId" readonly
                            [value]="evidenceFileName(returnEvidenceKey, custodyForm(allocation).evidenceUrl)">
                          <label [for]="'return-evidence-' + allocation.fieldEquipmentAllocationId">Return evidence</label>
                        </p-iftalabel>
                        <input #returnEvidence type="file" class="evidence-file-input"
                          accept="image/*,application/pdf,.doc,.docx,.xls,.xlsx,.txt"
                          (change)="selectEvidence($event, custodyForm(allocation), returnEvidenceKey)">
                        <button pButton type="button" icon="pi pi-upload" label="Upload" size="small"
                          [loading]="isEvidenceUploading(returnEvidenceKey)" (click)="returnEvidence.click()"></button>
                        @if (custodyForm(allocation).evidenceUrl) {
                          <button pButton type="button" icon="pi pi-times" severity="secondary" text
                            aria-label="Remove return evidence" (click)="clearEvidence(custodyForm(allocation), returnEvidenceKey)"></button>
                        }
                      </p-inputgroup>
                      <button pButton type="button" label="Return equipment" icon="pi pi-sign-in" size="small" outlined
                        [loading]="isAction('equipment-return-' + allocation.fieldEquipmentAllocationId)"
                        [disabled]="isEvidenceUploading(returnEvidenceKey)"
                        (click)="equipmentAction(allocation, 'return')"></button>
                    </div>
                  }

                  @if (allocation.allocationStatus === 'RETURNED') {
                    <div class="custody-summary">
                      <span>Outgoing <strong>{{ allocation.outgoingCondition || '—' }}</strong></span>
                      <span>Incoming <strong>{{ allocation.incomingCondition || '—' }}</strong></span>
                      <span>Damaged <strong>{{ allocation.damagedQuantity || 0 }}</strong></span>
                      <span>Lost <strong>{{ allocation.lostQuantity || 0 }}</strong></span>
                    </div>
                  }

                  @if (canExecute && ['ISSUED', 'IN_USE', 'RETURNED'].includes(allocation.allocationStatus || '')
                    && (!usage || usage.usageStatus === 'DRAFT' || usage.usageStatus === 'REJECTED')) {
                    @let usageEvidenceKey = evidenceKey('usage', allocation.fieldEquipmentAllocationId);
                    <div class="usage-block">
                      <strong>Actual equipment usage</strong>
                      <div class="actual-form">
                        <p-inputgroup>
                          <p-inputgroup-addon><i class="pi pi-calendar"></i></p-inputgroup-addon>
                          <p-iftalabel>
                            <p-datepicker [inputId]="'usage-work-date-' + allocation.fieldEquipmentAllocationId"
                              [(ngModel)]="equipmentDates[allocation.fieldEquipmentAllocationId!]" dateFormat="dd M yy"
                              appendTo="body" [showIcon]="true"></p-datepicker>
                            <label [for]="'usage-work-date-' + allocation.fieldEquipmentAllocationId">Work date</label>
                          </p-iftalabel>
                        </p-inputgroup>
                        <p-inputgroup>
                          <p-inputgroup-addon><i class="pi pi-calculator"></i></p-inputgroup-addon>
                          <p-iftalabel>
                            <p-inputNumber [inputId]="'usage-quantity-' + allocation.fieldEquipmentAllocationId"
                              [(ngModel)]="equipmentForm(allocation).actualQuantity" [min]="0.01"
                              [minFractionDigits]="0" [maxFractionDigits]="2"></p-inputNumber>
                            <label [for]="'usage-quantity-' + allocation.fieldEquipmentAllocationId">Actual quantity</label>
                          </p-iftalabel>
                        </p-inputgroup>
                        <p-inputgroup class="evidence-input-group">
                          <p-inputgroup-addon><i class="pi pi-paperclip"></i></p-inputgroup-addon>
                          <p-iftalabel>
                            <input pInputText [id]="'usage-evidence-' + allocation.fieldEquipmentAllocationId" readonly
                              [value]="evidenceFileName(usageEvidenceKey, equipmentForm(allocation).evidenceUrl)">
                            <label [for]="'usage-evidence-' + allocation.fieldEquipmentAllocationId">Evidence file</label>
                          </p-iftalabel>
                          <input #usageEvidence type="file" class="evidence-file-input"
                            accept="image/*,application/pdf,.doc,.docx,.xls,.xlsx,.txt"
                            (change)="selectEvidence($event, equipmentForm(allocation), usageEvidenceKey)">
                          <button pButton type="button" icon="pi pi-upload" label="Upload" size="small"
                            [loading]="isEvidenceUploading(usageEvidenceKey)" (click)="usageEvidence.click()"></button>
                          @if (equipmentForm(allocation).evidenceUrl) {
                            <button pButton type="button" icon="pi pi-times" severity="secondary" text
                              aria-label="Remove usage evidence" (click)="clearEvidence(equipmentForm(allocation), usageEvidenceKey)"></button>
                          }
                        </p-inputgroup>
                        <p-inputgroup>
                          <p-inputgroup-addon><i class="pi pi-file-edit"></i></p-inputgroup-addon>
                          <p-iftalabel>
                            <textarea pTextarea [id]="'usage-notes-' + allocation.fieldEquipmentAllocationId" rows="1"
                              [(ngModel)]="equipmentForm(allocation).notes"></textarea>
                            <label [for]="'usage-notes-' + allocation.fieldEquipmentAllocationId">Usage notes</label>
                          </p-iftalabel>
                        </p-inputgroup>
                      </div>
                      <div class="card-actions">
                        <button pButton type="button" label="Save usage" icon="pi pi-save" size="small"
                          [loading]="isAction('usage-save-' + allocation.fieldEquipmentAllocationId)"
                          [disabled]="isEvidenceUploading(usageEvidenceKey)"
                          (click)="saveEquipmentUsage(allocation)"></button>
                        @if (usage; as savedUsage) {
                          <button pButton type="button" label="Submit for approval" icon="pi pi-send" size="small" outlined
                            [loading]="isAction('usage-submit-' + savedUsage.fieldEquipmentUsageId)"
                            (click)="usageAction(savedUsage, 'submit')"></button>
                        }
                      </div>
                      @if (usage?.usageStatus === 'REJECTED') {
                        <small class="rejection-note">Rejected: {{ usage?.rejectionReason || 'No reason provided' }}</small>
                      }
                    </div>
                  }

                  @if (usage?.usageStatus === 'SUBMITTED' && canApprove) {
                    <div class="decision-row">
                      <p-inputgroup>
                        <p-inputgroup-addon><i class="pi pi-comment"></i></p-inputgroup-addon>
                        <p-iftalabel>
                          <input pInputText [id]="'usage-decision-' + usage?.fieldEquipmentUsageId"
                            [(ngModel)]="decisionReasons[decisionKey('equipment', usage?.fieldEquipmentUsageId)]">
                          <label [for]="'usage-decision-' + usage?.fieldEquipmentUsageId">Reason if rejecting</label>
                        </p-iftalabel>
                      </p-inputgroup>
                      <button pButton type="button" label="Reject" icon="pi pi-times" severity="danger" outlined size="small"
                        [loading]="isAction('usage-reject-' + usage?.fieldEquipmentUsageId)"
                        (click)="usageAction(usage!, 'reject')"></button>
                      <button pButton type="button" label="Approve" icon="pi pi-check" size="small"
                        [loading]="isAction('usage-approve-' + usage?.fieldEquipmentUsageId)"
                        (click)="usageAction(usage!, 'approve')"></button>
                    </div>
                  }

                  @if (usage?.usageStatus === 'APPROVED' && canApprove) {
                    <div class="decision-row reversal-row">
                      <p-inputgroup>
                        <p-inputgroup-addon><i class="pi pi-comment"></i></p-inputgroup-addon>
                        <p-iftalabel>
                          <input pInputText [id]="'usage-reversal-' + usage?.fieldEquipmentUsageId"
                            [(ngModel)]="decisionReasons[decisionKey('equipment', usage?.fieldEquipmentUsageId)]">
                          <label [for]="'usage-reversal-' + usage?.fieldEquipmentUsageId">Correction or reversal reason</label>
                        </p-iftalabel>
                      </p-inputgroup>
                      <button pButton type="button" label="Reverse usage" icon="pi pi-undo" severity="danger" outlined size="small"
                        [loading]="isAction('usage-reverse-' + usage?.fieldEquipmentUsageId)"
                        (click)="usageAction(usage!, 'reverse')"></button>
                    </div>
                  }

                  @if (canViewRates && usage) {
                    <div class="amounts">
                      <span>Quantity <strong>{{ usage.actualQuantity }} {{ usage.billingUnit }}</strong></span>
                      <span>Cost <strong>{{ usage.buyAmount | number:'1.2-2' }} {{ allocation.currencyCode }}</strong></span>
                      <span>Revenue <strong>{{ usage.sellAmount | number:'1.2-2' }} {{ allocation.currencyCode }}</strong></span>
                    </div>
                  }
                </article>
              } @empty {
                <div class="empty-state"><i class="pi pi-wrench"></i><strong>No reserved equipment</strong>
                  <span>This package has no reusable equipment custody to manage.</span></div>
              }
            </div>
          </section>
        }
      }
    </div>
  </section>

  <ng-template pTemplate="footer">
    <div class="master-dialog-footer">
      <button pButton type="button" label="Close" icon="pi pi-times" severity="secondary" outlined
        [disabled]="busy()" (click)="close()"></button>
      <span class="footer-spacer"></span>
      @if (canRelease && status === 'RELEASED') {
        <button pButton type="button" label="Start field work" icon="pi pi-play"
          [loading]="isAction('package-IN_PROGRESS')" (click)="packageAction('IN_PROGRESS')"></button>
      }
      @if (canRelease && status === 'IN_PROGRESS') {
        <button pButton type="button" label="Complete package" icon="pi pi-check-circle"
          [disabled]="!canComplete()" [loading]="isAction('package-COMPLETED')"
          (click)="packageAction('COMPLETED')"></button>
      }
    </div>
  </ng-template>
</p-dialog>
